import { Button, CircularProgress, Divider, Grid, Typography, Box, Paper, IconButton, Avatar, Stack, Chip } from "@mui/material";
import { Close, AdminPanelSettingsRounded, HistoryRounded, SecurityRounded, ListAltRounded, ManageAccountsRounded } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useQuery } from "react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import moment from "moment";

// Roles Details View Component
const RolesDetail = ({ handleViewModalClose, roleName }) => {
    const { enqueueSnackbar } = useSnackbar();
    const axiosPrivate = useAxiosPrivate();

    const { isLoading, data, isFetching, error } = useQuery(["role_detail", roleName], () => {
        return axiosPrivate.get(`/roles/${roleName}`);
    }, { refetchOnWindowFocus: false });

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error?.response?.data?.error?.message || error?.message || "Network Error!", {
                variant: "error",
                autoHideDuration: 2000
            });
        }
    }, [enqueueSnackbar, error]);

    const role = data?.data?.data;

    return (
        <Grid container sx={{ minHeight: "100vh", justifyContent: "center", alignItems: { xs: "flex-end", sm: "center" }, p: { xs: 0, sm: 2 } }}>
            <Paper
                elevation={0}
                sx={{
                    width: '100%',
                    maxWidth: { xs: '100%', sm: '500px' },
                    borderRadius: { xs: '24px 24px 0 0', sm: 6 },
                    boxShadow: "0px 20px 60px rgba(0, 0, 0, 0.15)",
                    overflow: 'hidden',
                    position: 'relative',
                    maxHeight: '95vh',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <IconButton
                    onClick={handleViewModalClose}
                    sx={{ position: 'absolute', right: 16, top: 16, color: 'text.disabled', zIndex: 1 }}
                >
                    <Close />
                </IconButton>

                <Box sx={{ p: { xs: 3, sm: 4 }, overflowY: 'auto' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                        <Avatar
                            sx={{
                                width: 80,
                                height: 80,
                                bgcolor: role?.name === 'super-user' ? 'error.lighter' : 'primary.lighter',
                                boxShadow: `0 8px 25px ${role?.name === 'super-user' ? 'rgba(211,47,47,0.1)' : 'rgba(255,170,0,0.1)'}`,
                                mb: 2,
                                borderRadius: 3
                            }}
                        >
                            <AdminPanelSettingsRounded sx={{ fontSize: 40, color: role?.name === 'super-user' ? 'error.main' : 'primary.main' }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: 'secondary.main', letterSpacing: '-0.5px' }}>
                            {role?.name}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Chip
                                label={role?.status || 'Active'}
                                size="small"
                                sx={{
                                    fontWeight: 900,
                                    textTransform: 'uppercase',
                                    fontSize: '9px',
                                    bgcolor: role?.status === 'ACTIVE' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                                    color: role?.status === 'ACTIVE' ? '#2e7d32' : '#d32f2f'
                                }}
                            />
                            {role?.name === 'super-user' && (
                                <Chip label="Protected" size="small" variant="outlined" color="error" sx={{ height: 20, fontSize: '9px', fontWeight: 900 }} />
                            )}
                        </Stack>
                    </Box>

                    {isLoading || isFetching ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : (
                        <Stack spacing={3}>
                            <DetailSection title="Lifecycle Metrics">
                                <DetailItem icon={<HistoryRounded fontSize="small" />} label="Registered" value={moment(role?.created_at).format("LLL")} />
                                <DetailItem icon={<HistoryRounded fontSize="small" />} label="Last Modified" value={moment(role?.updated_at).format("LLL")} />
                            </DetailSection>

                            <DetailSection title="Authorization Matrix">
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, mb: 1, display: 'block' }}>
                                    Assigned Scopes & Permissions
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {role?.scopes?.map((scope, idx) => (
                                        <Chip
                                            key={idx}
                                            label={scope.name}
                                            size="small"
                                            icon={<SecurityRounded sx={{ fontSize: '12px !important' }} />}
                                            sx={{ borderRadius: 2, fontWeight: 700, px: 0.5, bgcolor: 'rgba(0,0,0,0.03)' }}
                                        />
                                    ))}
                                    {(!role?.scopes || role?.scopes.length === 0) && (
                                        <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>No granular scopes assigned.</Typography>
                                    )}
                                </Box>
                            </DetailSection>
                        </Stack>
                    )}

                    <Box sx={{ mt: 4 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            sx={{ borderRadius: 3, py: 1.5, fontWeight: 700, textTransform: 'none', bgcolor: 'secondary.main' }}
                            onClick={handleViewModalClose}
                        >
                            Close Overview
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Grid>
    );
};

function DetailSection({ title, children }) {
    return (
        <Box>
            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', mb: 1, display: 'block', letterSpacing: '1px' }}>
                {title}
            </Typography>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.01)', borderRadius: 4, border: '1px solid rgba(0,0,0,0.03)' }}>
                {children}
            </Paper>
        </Box>
    );
}

function DetailItem({ icon, label, value }) {
    return (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, '&:last-child': { mb: 0 } }}>
            <Box sx={{ color: 'primary.main', mt: 0.5, display: 'flex' }}>{icon}</Box>
            <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                    {value || 'N/A'}
                </Typography>
            </Box>
        </Box>
    );
}

export default RolesDetail;